import React, { useState, useEffect } from 'react';
import { db } from '../utils/firebase';
import { 
  doc, 
  updateDoc, 
  getDoc, 
  setDoc, 
  collection, 
  query, 
  where, 
  getDocs,
  writeBatch
} from 'firebase/firestore';
import { useAuth } from '../hooks/useAuth';

const AVATARS = [
  { id: 'moon', name: 'Crescent Moon', icon: '🌙' },
  { id: 'star', name: 'Shooting Star', icon: '🌠' },
  { id: 'cloud', name: 'Dream Cloud', icon: '☁️' },
  { id: 'sparkles', name: 'Magic Sparkles', icon: '✨' },
  { id: 'night', name: 'Starry Night', icon: '🌌' },
  { id: 'sunset', name: 'Mystic Sunset', icon: '🌅' },
  { id: 'rainbow', name: 'Dream Rainbow', icon: '🌈' },
  { id: 'comet', name: 'Cosmic Comet', icon: '☄️' },
  { id: 'galaxy', name: 'Distant Galaxy', icon: '🌌' },
  { id: 'aurora', name: 'Northern Lights', icon: '🌌' },
  { id: 'crystal', name: 'Magic Crystal', icon: '💎' },
  { id: 'lantern', name: 'Dream Lantern', icon: '🏮' }
];

interface ProfileSettingsProps {
  onClose: () => void;
}

const ProfileSettings: React.FC<ProfileSettingsProps> = ({ onClose }) => {
  const { user } = useAuth();
  const [selectedAvatar, setSelectedAvatar] = useState<string>('');
  const [displayName, setDisplayName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    const fetchCurrentProfile = async () => {
      if (!user) return;
      try {
        const userDoc = await getDoc(doc(db, 'users', user.uid));
        if (userDoc.exists()) {
          const data = userDoc.data();
          setDisplayName(data.displayName || '');
          setSelectedAvatar(data.avatar || '');
          setDescription(data.description || '');

          // Fix old dreams that might have initials instead of icons
          const dreamsRef = collection(db, 'dreams');
          const q = query(dreamsRef, where('userId', '==', user.uid));
          const querySnapshot = await getDocs(q);
          
          if (querySnapshot.size > 0) {
            const batch = writeBatch(db);
            querySnapshot.forEach((doc) => {
              const dream = doc.data();
              // Check if the userIcon is a single character (initial)
              if (!dream.userIcon || dream.userIcon.length === 1) {
                batch.update(doc.ref, {
                  userIcon: data.icon || '🌙'
                });
              }
            });
            await batch.commit();
          }
        }
      } catch (error) {
        console.error('Error fetching profile:', error);
      }
    };

    fetchCurrentProfile();
  }, [user]);

  const handleSave = async () => {
    if (!user) return;
    setIsSaving(true);

    try {
      const userRef = doc(db, 'users', user.uid);
      const selectedAvatarIcon = AVATARS.find(a => a.id === selectedAvatar)?.icon || '🌙';
      
      // First, ensure the user document exists
      const userDoc = await getDoc(userRef);
      if (!userDoc.exists()) {
        // Create the initial user document if it doesn't exist
        await setDoc(userRef, {
          displayName: displayName.trim(),
          description: description.trim(),
          avatar: selectedAvatar,
          icon: selectedAvatarIcon,
          createdAt: new Date(),
          updatedAt: new Date()
        });
      } else {
        // Update the existing user document
        await updateDoc(userRef, {
          displayName: displayName.trim(),
          description: description.trim(),
          avatar: selectedAvatar,
          icon: selectedAvatarIcon,
          updatedAt: new Date()
        });
      }

      // Update all dreams with the new display name and icon
      const dreamsRef = collection(db, 'dreams');
      const q = query(dreamsRef, where('userId', '==', user.uid));
      const querySnapshot = await getDocs(q);
      
      const batch = writeBatch(db);
      querySnapshot.forEach((doc) => {
        batch.update(doc.ref, {
          authorName: displayName.trim(),
          userIcon: selectedAvatarIcon
        });
      });
      await batch.commit();

      onClose();
      window.location.reload();
    } catch (error) {
      console.error('Error updating profile:', error);
    } finally {
      setIsSaving(false);
    }
  };

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  return (
    <div className="fixed inset-0 z-[100] flex items-start sm:items-center justify-center">
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      <div 
        className="relative w-full h-full sm:h-auto sm:max-h-[90vh] sm:max-w-md sm:mx-4 bg-dream-900/95 border border-dream-700/30 sm:rounded-xl shadow-xl backdrop-blur-lg overflow-y-auto"
        onClick={e => e.stopPropagation()}
      >
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-br from-dream-500/5 via-transparent to-mist-500/5 opacity-50" />

        {/* Content */}
        <div className="relative p-4 sm:p-6 space-y-4">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl sm:text-2xl font-bold text-dream-50">
              Customize Your Profile
            </h2>
            <button
              onClick={onClose}
              className="p-2 text-dream-400 hover:text-dream-200 transition-colors"
            >
              ✕
            </button>
          </div>

          <div className="space-y-4">
            <div>
              <label htmlFor="displayName" className="block text-sm font-medium text-dream-200 mb-2">
                Display Name
              </label>
              <input
                type="text"
                id="displayName"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-dream-800/50 border border-dream-700/30
                         text-dream-50 placeholder-dream-400/50 focus:outline-none focus:ring-2
                         focus:ring-dream-500/50 focus:border-dream-600/50 transition-all duration-300
                         hover:border-dream-600/50"
                placeholder="Choose your display name..."
                maxLength={20}
              />
            </div>

            <div>
              <label htmlFor="description" className="block text-sm font-medium text-dream-200 mb-2">
                Description
              </label>
              <textarea
                id="description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="w-full px-3 py-2 rounded-lg bg-dream-800/50 border border-dream-700/30
                         text-dream-50 placeholder-dream-400/50 focus:outline-none focus:ring-2
                         focus:ring-dream-500/50 focus:border-dream-600/50 transition-all duration-300
                         hover:border-dream-600/50 resize-none"
                placeholder="Tell us about yourself..."
                rows={3}
                maxLength={100}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-dream-200 mb-2">
                Choose Your Avatar
              </label>
              <div className="grid grid-cols-3 gap-2">
                {AVATARS.map((avatar) => (
                  <button
                    key={avatar.id}
                    onClick={() => setSelectedAvatar(avatar.id)}
                    className={`p-2 rounded-xl text-2xl transition-all duration-300
                              ${selectedAvatar === avatar.id 
                                ? 'bg-dream-700/70 border-2 border-dream-500 scale-105' 
                                : 'bg-dream-800/50 border border-dream-700/30 hover:scale-105'}
                              hover:border-dream-600/50`}
                  >
                    <span className="block text-center">{avatar.icon}</span>
                    <span className="block text-[10px] sm:text-xs text-dream-300 mt-1 truncate">{avatar.name}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2 pt-4">
            <button
              onClick={onClose}
              className="w-full sm:w-auto px-4 py-2 rounded-lg bg-dream-800/50 text-dream-200 
                       hover:bg-dream-800/70 transition-all duration-300
                       border border-dream-700/30 hover:border-dream-600/50"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={isSaving || !selectedAvatar || !displayName.trim()}
              className="w-full sm:w-auto px-4 py-2 rounded-lg bg-dream-600/50 text-dream-50 
                       hover:bg-dream-600/70 transition-all duration-300
                       border border-dream-500/30 hover:border-dream-400/50
                       disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving ? (
                <span className="flex items-center justify-center gap-2">
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Saving...
                </span>
              ) : (
                'Save Changes'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfileSettings; 